import {
  ARCHIVAL_REASON_OPTIONS,
  DOWNGRADE_NOTES_MAX_LENGTH,
  type ArchivalReason,
  type DowngradeFormValues,
  type DowngradeRequest,
  type DowngradeResult,
  type DowngradeTargetPlan,
} from '@/schemas/downgrade';
import { BillingPlanEnum } from '@/types/organizations';

export const DOWNGRADE_PERMISSION_ERROR = "You don't have permission to downgrade organizations.";
export const DOWNGRADE_UNAVAILABLE_ERROR =
  "Couldn't reach Chargebee. Nothing was changed. Try again, and contact engineering if it keeps failing.";
export const DOWNGRADE_NOTE_WRITE_FAILED_ERROR =
  'The downgrade was applied in Chargebee but the Admin note could not be saved. Retry to finish.';
export const DOWNGRADE_GENERIC_ERROR =
  'Something went wrong. Please try again. If the problem continues, contact engineering.';

const PLAN_LABELS: Record<`${BillingPlanEnum}`, string> = {
  [BillingPlanEnum.FREE]: 'Free',
  [BillingPlanEnum.GROWTH]: 'Starter',
  [BillingPlanEnum.STARTER]: 'Starter',
  [BillingPlanEnum.SCALE]: 'Scale',
  [BillingPlanEnum.PREMIUM]: 'Premium',
};

/** Accepts the wire value, so a `DowngradeTargetPlan` needs no conversion to `BillingPlanEnum`. */
export const planLabel = (plan: `${BillingPlanEnum}`): string => PLAN_LABELS[plan];

/**
 * UTC calendar date as `MMM D, YYYY`, matching the backend `format_term_date`.
 * Chargebee term ends are UTC timestamps, so the viewer's time zone must not shift the day.
 */
export const formatTermDate = (isoTimestamp: string | null | undefined): string => {
  if (!isoTimestamp) {
    return '';
  }
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

export const getArchivalReasonLabel = (reason: ArchivalReason | null): string =>
  ARCHIVAL_REASON_OPTIONS.find(option => option.value === reason)?.label ?? '';

/** Only a Free target can carry an archive, so a stale archive choice is dropped for Starter. */
export const buildDowngradeRequest = (
  values: DowngradeFormValues & { target_plan: DowngradeTargetPlan }
): DowngradeRequest => {
  const archive = values.target_plan === 'FREE' && values.archive;
  const notes = values.archival_reason_notes.trim().slice(0, DOWNGRADE_NOTES_MAX_LENGTH);
  return {
    target_plan: values.target_plan,
    archive,
    archival_reason: archive ? values.archival_reason : null,
    archival_reason_notes: archive && notes !== '' ? notes : null,
  };
};

type ReviewSummaryInput = {
  currentPlan: BillingPlanEnum;
  request: DowngradeRequest;
  /** `current_term_end` from the Admin subscription read; never recomputed in the UI. */
  currentTermEnd: string | null | undefined;
  organizationName: string;
};

export type DowngradeReviewSummary = {
  title: string;
  alert: string;
  fromLabel: string;
  toLabel: string;
  planChangesOn: string;
  archiveValue: string;
  reasonLabel: string | null;
  /** Free-text note the admin typed; null when none was provided. */
  notes: string | null;
};

const reviewTitle = (request: DowngradeRequest): string => {
  if (request.archive) {
    return 'Review Downgrade and Archive';
  }
  return `Review Downgrade to ${request.target_plan === 'GROWTH' ? 'Starter' : 'Free'}`;
};

/** Premium changes at the end of the term; Starter (Growth) cancels right away. */
export const buildReviewSummary = ({
  currentPlan,
  request,
  currentTermEnd,
  organizationName,
}: ReviewSummaryInput): DowngradeReviewSummary => {
  const endsOfTerm = currentPlan === BillingPlanEnum.PREMIUM;
  const termDate = formatTermDate(currentTermEnd);

  let alert = 'This updates the subscription in Chargebee. The plan in Kintsugi changes once Chargebee applies it.';
  if (!endsOfTerm) {
    alert = request.archive
      ? `This cancels the subscription in Chargebee right away. ${organizationName} is archived once Chargebee applies the cancellation.`
      : `This cancels the subscription in Chargebee right away. ${organizationName} stays active on Free.`;
  }

  let archiveValue = 'No';
  if (request.archive) {
    archiveValue = endsOfTerm && termDate ? `Yes - On ${termDate}` : 'Yes';
  }

  let planChangesOn = 'Immediately';
  if (endsOfTerm) {
    planChangesOn = termDate ? `End of current term, ${termDate}` : 'End of current term';
  }

  return {
    title: reviewTitle(request),
    alert,
    fromLabel: planLabel(currentPlan),
    toLabel: planLabel(request.target_plan),
    planChangesOn,
    archiveValue,
    reasonLabel: request.archive ? getArchivalReasonLabel(request.archival_reason) : null,
    notes: request.archive ? request.archival_reason_notes : null,
  };
};

/** Success toast copy per CP-5816. The date always comes from the response, never from the UI. */
export const getDowngradeSuccessMessage = (result: DowngradeResult, organizationName: string): string => {
  const date = formatTermDate(result.current_term_end);
  const target = planLabel(result.to_plan);

  if (result.from_plan === 'GROWTH') {
    if (!result.archive) {
      return 'Downgraded to Free.';
    }
    return `Downgrade to Free and archive scheduled. On cancel apply, ${organizationName} is archived. Until then, it stays active.`;
  }

  if (result.archive) {
    return date ? `Downgrade to Free and archive scheduled for ${date}.` : 'Downgrade to Free and archive scheduled.';
  }

  return date ? `Downgrade to ${target} scheduled for ${date}.` : `Downgrade to ${target} scheduled.`;
};

export type ScheduledFreeDowngrade = {
  /** `current_term_end` formatted for display; never computed in the UI. */
  date: string;
  /** True when the organization also has a pending archive. */
  withArchive: boolean;
};

type ScheduledFreeDowngradeInput = {
  subscriptionStatus: string | null | undefined;
  currentTermEnd: string | null | undefined;
  /** Pending archive timestamp from the organization; absent until the backend ships the field. */
  pendingArchiveAt: string | null | undefined;
};

/**
 * A `non_renewing` Chargebee subscription means a cancel to Free is scheduled at `current_term_end`.
 * Without a term end there is no date to show, so nothing is reported.
 */
export const getScheduledFreeDowngrade = ({
  subscriptionStatus,
  currentTermEnd,
  pendingArchiveAt,
}: ScheduledFreeDowngradeInput): ScheduledFreeDowngrade | null => {
  const date = formatTermDate(currentTermEnd);
  if (subscriptionStatus !== 'non_renewing' || !date) {
    return null;
  }
  return { date, withArchive: Boolean(pendingArchiveAt) };
};

// TODO: confirm wording with design (no Figma frame for the in-progress state)
export const STARTER_CANCEL_IN_PROGRESS_LABEL = 'Change to Free in progress';

export const STARTER_CANCEL_POLL_INTERVAL_MS = 5000;
export const STARTER_CANCEL_POLL_MAX_MS = 5 * 60 * 1000;

/**
 * A Starter to Free downgrade cancels in Chargebee at once, but the plan flips to Free only when the
 * `subscription_cancelled` webhook lands. Until then the subscription read says `cancelled` while the
 * organization is still on Starter (Growth).
 */
export const isStarterCancelInProgress = (
  billingPlan: BillingPlanEnum | null | undefined,
  subscriptionStatus: string | null | undefined
): boolean => billingPlan === BillingPlanEnum.GROWTH && subscriptionStatus === 'cancelled';

/**
 * Poll every 5 seconds while the cancel is in progress, and give up after 5 minutes so a lost webhook
 * cannot keep the admin panel polling forever. `startedAtMs` is when the state was first seen.
 */
export const getStarterCancelPollInterval = (
  inProgress: boolean,
  startedAtMs: number | null,
  nowMs: number = Date.now()
): number | false => {
  if (!inProgress || startedAtMs == null || nowMs - startedAtMs >= STARTER_CANCEL_POLL_MAX_MS) {
    return false;
  }
  return STARTER_CANCEL_POLL_INTERVAL_MS;
};

/** "Changes to <plan> on <term end>"; null without a term end because the date is never computed in the UI. */
export const getScheduledPlanLabel = (
  plan: DowngradeTargetPlan,
  currentTermEnd: string | null | undefined
): string | null => {
  const date = formatTermDate(currentTermEnd);
  return date ? `Changes to ${planLabel(plan)} on ${date}` : null;
};

export const getScheduledArchiveBannerText = (
  scheduled: ScheduledFreeDowngrade,
  organizationName: string,
  currentPlan: BillingPlanEnum
): string =>
  `Downgrade to Free and archive scheduled for ${scheduled.date}. On that date, the Chargebee subscription is cancelled and ${organizationName} is archived. Until then, it stays active on ${planLabel(currentPlan)}.`;

type ApiErrorShape = {
  response?: { status?: number; data?: { detail?: unknown } };
};

/** The downgrade API sends `detail` as `{ error, code }` or `{ message, code }`; guards send a plain string. */
const readDetailText = (detail: unknown): string | null => {
  if (typeof detail === 'string' && detail !== '') {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    const { error, message } = detail as { error?: unknown; message?: unknown };
    for (const candidate of [error, message]) {
      if (typeof candidate === 'string' && candidate !== '') {
        return candidate;
      }
    }
  }
  return null;
};

export const getDowngradeErrorMessage = (error: unknown): string => {
  const { response } = (error ?? {}) as ApiErrorShape;
  const status = response?.status;
  const detail = response?.data?.detail;
  const code = detail && typeof detail === 'object' ? (detail as { code?: unknown }).code : undefined;

  if (status === 401 || status === 403) {
    return DOWNGRADE_PERMISSION_ERROR;
  }
  if (status === 502 || code === 'CHARGEBEE_UNAVAILABLE') {
    return DOWNGRADE_UNAVAILABLE_ERROR;
  }
  if (code === 'NOTE_WRITE_FAILED') {
    return DOWNGRADE_NOTE_WRITE_FAILED_ERROR;
  }
  if (status === 400 || status === 409 || status === 422) {
    return readDetailText(detail) ?? DOWNGRADE_GENERIC_ERROR;
  }
  return DOWNGRADE_GENERIC_ERROR;
};
